import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {
    ArrowLeftOutlined,
    DeleteOutlined,
    EditOutlined,
    GithubOutlined,
    LoadingOutlined,
    ProjectOutlined,
    UnorderedListOutlined,
    ExclamationCircleOutlined,
    CloseOutlined,
    MessageFilled,
    FileProtectOutlined,
    CloudDownloadOutlined,
    ControlOutlined
} from '@ant-design/icons';
import styles from '@/styles/pages/projects/ProjectDetail.module.css';
import {projectsAPI} from "@/api/endpoints/projects.js";
import ProjectCustomPage from './ProjectCustomPage.jsx';

const ProjectDetail = () => {
    const navigate = useNavigate();
    const {uuid} = useParams();
    const [loading, setLoading] = useState(true);
    const [project, setProject] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false); // Состояние для модального окна

    // Статусы проектов
    const statuses = {
        active: {label: 'Активен', color: '#3fb950'},
        development: {label: 'В разработке', color: '#d29922'},
        staging: {label: 'Тестирование', color: '#8957e5'},
        archived: {label: 'Архивирован', color: '#7d8590'}
    };

    useEffect(() => {
        document.title = project ? `${project.name} | DevSpace` : 'Загрузка проекта...';
    }, [project]);

    useEffect(() => {
        fetchProject();
    }, [uuid]);

    const fetchProject = async () => {
        setLoading(true);
        try {
            const response = await projectsAPI.get(uuid, false);
            if (response.success && response.data) {
                setProject(response.data);
            } else {
                // Проект не найден
                navigate('/404');
            }
        } catch (error) {
            console.error('Error fetching project:', error);
        } finally {
            setLoading(false);
        }
    };

    // Навигация
    const handleBack = () => {
        navigate(-1);
    };

    const handleEdit = () => {
        navigate(`/projects/${uuid}/edit`);
    };

    const handleKanban = () => {
        navigate(`/projects/${uuid}/kanban`);
    };

    const handleOpenPasswords = () => {
        navigate(`/projects/${uuid}/passwords`);
    };

    const handleValheim = () => {
        navigate(`/projects/${uuid}/valheim`);
    };

    const handleBackups = () => {
        navigate(`/projects/${uuid}/backups`);
    };

    const handleOpenTelegram = () => {
        navigate(`/projects/${uuid}/tg-logs`);
    };

    // Открыть модальное окно
    const openDeleteModal = () => {
        setShowDeleteModal(true);
    };

    // Закрыть модальное окно
    const closeDeleteModal = () => {
        setShowDeleteModal(false);
    };

    const handleDelete = async () => {
        setDeleteLoading(true);
        try {
            const response = await projectsAPI.delete(uuid);
            if (response.success) {
                navigate('/projects', {replace: true});
            } else {
                alert(response.message || 'Не удалось удалить проект');
            }
        } catch (error) {
            alert('Произошла ошибка при удалении проекта');
        } finally {
            setDeleteLoading(false);
            setShowDeleteModal(false);
        }
    };

    // Показ загрузки
    if (loading) {
        return (
            <div className={styles.container}>
                <div className={styles.loadingContainer}>
                    <LoadingOutlined className={styles.loadingSpinner}/>
                    <p>Загрузка проекта...</p>
                </div>
            </div>
        );
    }

    if (!project) {
        return null;
    }

    return (
        <div className={styles.container}>
            {/* Фоновые декорации */}
            <div className={styles.bgGradient}></div>
            <div className={styles.bgGrid}></div>

            <div className={styles.content}>
                {/* Шапка */}
                <div className={styles.header}>
                    <button className={styles.backButton} onClick={handleBack}>
                        <ArrowLeftOutlined/> Назад
                    </button>
                    <div className={styles.headerActions}>
                        <button className={styles.editButton} onClick={handleEdit}>
                            <EditOutlined/> Редактировать
                        </button>
                        <button className={styles.deleteButton} onClick={openDeleteModal} disabled={deleteLoading}>
                            <DeleteOutlined/> {deleteLoading ? 'Удаление...' : 'Удалить'}
                        </button>
                    </div>
                </div>

                {/* Информация о проекте */}
                <div className={styles.projectHeader}>
                    <div className={styles.projectIcon} style={{background: `var(--color-accent-primary)15`}}>
                        <ProjectOutlined style={{color: `var(--color-accent-primary)`, fontSize: 48}}/>
                    </div>
                    <div className={styles.projectInfo}>
                        <h1 className={styles.projectName}>{project.name}</h1>
                        <div className={styles.projectMeta}>
                      <span
                          className={styles.projectStatus}
                          style={{
                              backgroundColor: `${statuses[project.status]?.color}15`,
                              color: statuses[project.status]?.color
                          }}
                      >
                        {statuses[project.status]?.label || project.status}
                      </span>
                            {project.repository_url && (
                                <a
                                    href={project.repository_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.projectLink}
                                >
                                    <GithubOutlined/> Репозиторий
                                </a>
                            )}
                        </div>
                        {project.description && (
                            <p className={styles.projectDescription}>{project.description}</p>
                        )}
                    </div>
                </div>

                {/* Кнопки действий */}
                <div className={styles.actionsGrid}>
                    <button className={styles.actionCard} onClick={handleKanban}>
                        <div className={styles.actionIcon} style={{color: '#58a6ff'}}>
                            <UnorderedListOutlined/>
                        </div>
                        <div className={styles.actionInfo}>
                            <h3 className={styles.actionTitle}>Канбан</h3>
                            <p className={styles.actionDescription}>Управление задачами и спринтами</p>
                        </div>
                    </button>

                    <button className={styles.actionCard} onClick={handleEdit}>
                        <div className={styles.actionIcon} style={{color: '#3fb950'}}>
                            <EditOutlined/>
                        </div>
                        <div className={styles.actionInfo}>
                            <h3 className={styles.actionTitle}>Редактировать проект</h3>
                            <p className={styles.actionDescription}>Изменить настройки проекта</p>
                        </div>
                    </button>

                    <button className={styles.actionCard} onClick={handleOpenTelegram}>
                        <div className={styles.actionIcon} style={{color: '#8957e5'}}>
                            <MessageFilled/>
                        </div>
                        <div className={styles.actionInfo}>
                            <h3 className={styles.actionTitle}>Логи телеграмма</h3>
                            <p className={styles.actionDescription}>Просмотр логов телеграмма</p>
                        </div>
                    </button>

                    <button className={styles.actionCard} onClick={handleOpenPasswords}>
                        <div className={styles.actionIcon} style={{color: '#8957e5'}}>
                            <FileProtectOutlined/>
                        </div>
                        <div className={styles.actionInfo}>
                            <h3 className={styles.actionTitle}>Пароли проекта</h3>
                            <p className={styles.actionDescription}>Просмотр паролей проекта</p>
                        </div>
                    </button>

                    <button className={styles.actionCard} onClick={handleBackups}>
                        <div className={styles.actionIcon} style={{color: '#3fb950'}}>
                            <CloudDownloadOutlined/>
                        </div>
                        <div className={styles.actionInfo}>
                            <h3 className={styles.actionTitle}>Бэкапы</h3>
                            <p className={styles.actionDescription}>Бэкап базы проекта: скачать или удалить</p>
                        </div>
                    </button>

                    {/valheim/i.test(project.name) && (
                        <button className={styles.actionCard} onClick={handleValheim}>
                            <div className={styles.actionIcon} style={{color: '#d29922'}}>
                                <ControlOutlined/>
                            </div>
                            <div className={styles.actionInfo}>
                                <h3 className={styles.actionTitle}>Управление сервером</h3>
                                <p className={styles.actionDescription}>Статус, whitelist, баны, кик, перезапуск</p>
                            </div>
                        </button>
                    )}

                </div>

                {/* Кастомная страница проекта */}
                <ProjectCustomPage page={project.custom_page}/>

                {/* Дополнительная информация (опционально) */}
                <div className={styles.additionalInfo}>
                    <div className={styles.infoCard}>
                        <h4 className={styles.infoTitle}>Стек технологий</h4>
                        <div className={styles.techStack}>
                            {project.tech_stack && project.tech_stack.length > 0 ? (
                                project.tech_stack.map((tech, idx) => (
                                    <span key={idx} className={styles.techBadge}>{tech}</span>
                                ))
                            ) : (
                                <span className={styles.emptyText}>Не указан</span>
                            )}
                        </div>
                    </div>

                    <div className={styles.infoCard}>
                        <h4 className={styles.infoTitle}>Дата создания</h4>
                        <p className={styles.infoText}>
                            {new Date(project.created_at).toLocaleDateString('ru-RU', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                        </p>
                    </div>

                    <div className={styles.infoCard}>
                        <h4 className={styles.infoTitle}>Последнее обновление</h4>
                        <p className={styles.infoText}>
                            {new Date(project.updated_at).toLocaleDateString('ru-RU', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            })}
                        </p>
                    </div>
                </div>
            </div>

            {/* Модальное окно удаления */}
            {showDeleteModal && (
                <div className={styles.modalOverlay} onClick={closeDeleteModal}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <div className={styles.modalIcon}>
                                <ExclamationCircleOutlined/>
                            </div>
                            <h3 className={styles.modalTitle}>Удаление проекта</h3>
                            <button className={styles.modalClose} onClick={closeDeleteModal}>
                                <CloseOutlined/>
                            </button>
                        </div>

                        <div className={styles.modalBody}>
                            <p>Вы действительно хотите удалить проект <strong>"{project.name}"</strong>?</p>
                            <p className={styles.modalWarning}>
                                Это действие невозможно отменить. Все данные проекта будут безвозвратно удалены.
                            </p>
                        </div>

                        <div className={styles.modalFooter}>
                            <button className={styles.modalCancelButton} onClick={closeDeleteModal}>
                                Отмена
                            </button>
                            <button
                                className={styles.modalDeleteButton}
                                onClick={handleDelete}
                                disabled={deleteLoading}
                            >
                                {deleteLoading ? 'Удаление...' : 'Да, удалить'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectDetail;